import * as React from "react";

import { identity, maybe } from "../utils/functional";

import { buildAddressComponents } from "./utils";
import { eventValue } from "../utils/browser";
import { useBlueZebraAddressSearch } from "./useBlueZebraAddressSearch";

export function useBlueZebraAddressInput(args: TODO = {}): TODO {
  const {
    value,
    onChange,
    error,
    onError,
    isValidating,
    onValidating = identity,
    integrations,
  } = args;
  const [searchTerm, setSearchTerm] = React.useState("");

  const {
    data,
    loading: suggestionsLoading,
    clearSuggestions,
  } = useBlueZebraAddressSearch({
    searchTerm,
  });

  // Note, this only communicates failed validations; does not confirm passing validation.
  async function validateAddress(value) {
    if (!value) return value;
    maybe(onError)(null);
    onValidating(true);
    try {
      const place = await geocodePlaceId({
        lat: parseFloat(value.glatitude || value.Glatitude),
        lng: parseFloat(value.glongitude || value.Glongitude),
      });

      const detailedAddress = {
        ...value,
        addressComponents: place.address_components,
        formattedAddress: place.formatted_address,
        // TODO: Should we call geometry.location.lat() and .lng() for actual number values here?
        geometry: place.geometry,
      };

      const bzAddressOverwrites = {
        formattedAddress: value.AddressText,
        postcode: value.PostCode,
        state: value.State,
        streetLine: value.AddressLine1,
        suburb: value.Suburb,
      };

      if (!getComponentValue(detailedAddress, "postal_code")) {
        throw new Error("Address must have postcode");
      }
      maybe(onChange)({
        ...detailedAddress,
        ...buildAddressComponents(detailedAddress),
        ...bzAddressOverwrites,
      });
    } catch (e) {
      clearSuggestions();
      maybe(onError)(e?.message);
    } finally {
      onValidating(false);
    }
  }

  async function handleChange(value) {
    if (value) {
      value = {
        ...value,
        id: value.Gnaf,
        description: value.AddressText,
        // BZ address search return lat and long in reverse order
        // We need to swap them to match the google format
        glatitude: value.Longitude,
        glongitude: value.Latitude,
        addressComponents: {
          postal_code: value.PostCode,
        },
      };
    }
    maybe(onChange)(value);
    validateAddress(value);
  }

  React.useEffect(() => {
    if (value) {
      validateAddress(value);
    }
  }, []);

  return {
    onInputChange: eventValue(setSearchTerm),
    onChange: handleChange,
    disabled: isValidating,
    options: data,
    isLoading: suggestionsLoading || isValidating,
    error: !!error,
  };
}

function geocodePlaceId(location) {
  return new Promise<TODO>((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location }, (results, status) => {
      if (status == "OK" && results[0]) {
        resolve(results[0]);
      }
      reject();
    });
  });
}

function getComponentValue(address, type) {
  const component =
    address.addressComponents.find((c) => c.types.find((t) => t == type)) || {};
  return component.short_name;
}
